import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeSearchQuery } from '../utils/sanitizeSearchQuery';
import ReportMatchModal from '../components/ReportMatchModal';
import './Friends.css';

/* ─────────────────────────────────────────────────────────────
   FRIENDS PAGE
   Lets users manage their friends/rivals: view accepted friends,
   respond to incoming requests, cancel sent requests, search for
   new people, and share an invite link.

   Invite link flow:
   - /friends?invite=<username> auto-sends a request from the
     logged-in user TO the person named in the param. The inviter
     shared their link; the visitor is the one clicking → visitor
     sends TO inviter.
   ───────────────────────────────────────────────────────────── */
function Friends() {
  const { user }         = useAuth();
  const [searchParams]   = useSearchParams();
  const navigate         = useNavigate();

  // ── Core data ──────────────────────────────────────────────
  const [myUsername, setMyUsername] = useState('');
  const [friends,    setFriends]    = useState([]);   // accepted
  const [received,   setReceived]   = useState([]);   // incoming pending
  const [sent,       setSent]       = useState([]);   // outgoing pending
  const [loading,    setLoading]    = useState(true);

  // ── UI state ───────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('friends');

  // ── Player search ──────────────────────────────────────────
  const [query,         setQuery]         = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Map of profileId → 'accepted' | 'pending' | undefined
  const [searchStatuses, setSearchStatuses] = useState({});

  // ── Invite link ────────────────────────────────────────────
  const [copied, setCopied] = useState(false);

  // ── Report match modal ─────────────────────────────────────
  const [reportModalOpen,   setReportModalOpen]   = useState(false);
  const [reportTarget,      setReportTarget]      = useState(null);

  // ── Load everything on mount ───────────────────────────────
  useEffect(() => {
    if (!user) return;
    loadAll();
    fetchMyUsername();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Process ?invite= param on mount ───────────────────────
  useEffect(() => {
    const inviteUsername = searchParams.get('invite');
    if (!inviteUsername || !user) return;
    processInvite(inviteUsername);
  }, [searchParams, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Search: debounced 300ms ────────────────────────────────
  useEffect(() => {
    if (query.trim().length < 2) { setSearchResults([]); return; }
    setSearchLoading(true);
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, username, elo_rating, matches_played')
        .or(`full_name.ilike.%${sanitizeSearchQuery(query.trim())}%,username.ilike.%${sanitizeSearchQuery(query.trim())}%`)
        .neq('id', user.id)
        .limit(6);

      const results = data || [];
      setSearchResults(results);
      setSearchLoading(false);

      // Batch-fetch friendship statuses for all results
      if (results.length) {
        const ids = results.map(p => p.id);
        const [{ data: sentRows }, { data: receivedRows }] = await Promise.all([
          supabase.from('friendships').select('friend_id, status').eq('user_id', user.id).in('friend_id', ids),
          supabase.from('friendships').select('user_id, status').eq('friend_id', user.id).in('user_id', ids),
        ]);
        const statuses = {};
        (sentRows    || []).forEach(r => { statuses[r.friend_id] = r.status; });
        (receivedRows || []).forEach(r => { if (!statuses[r.user_id]) statuses[r.user_id] = r.status; });
        setSearchStatuses(statuses);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, user]);

  // ── Data fetchers ──────────────────────────────────────────
  async function fetchMyUsername() {
    const { data } = await supabase.from('profiles').select('username').eq('id', user.id).single();
    if (data?.username) setMyUsername(data.username);
  }

  async function loadAll() {
    setLoading(true);
    await Promise.all([fetchFriends(), fetchReceivedRequests(), fetchSentRequests()]);
    setLoading(false);
  }

  async function fetchFriends() {
    // Accepted friendships can be in either direction
    const [{ data: asSender }, { data: asReceiver }] = await Promise.all([
      supabase.from('friendships').select('friend_id').eq('user_id', user.id).eq('status', 'accepted'),
      supabase.from('friendships').select('user_id').eq('friend_id', user.id).eq('status', 'accepted'),
    ]);
    const ids = [
      ...(asSender   || []).map(r => r.friend_id),
      ...(asReceiver || []).map(r => r.user_id),
    ];
    if (!ids.length) { setFriends([]); return; }
    const { data: profiles } = await supabase
      .from('profiles').select('id, username, elo_rating, matches_played').in('id', ids);
    setFriends(profiles || []);
  }

  async function fetchReceivedRequests() {
    const { data: rows } = await supabase
      .from('friendships').select('id, user_id').eq('friend_id', user.id).eq('status', 'pending');
    if (!rows?.length) { setReceived([]); return; }
    const ids = rows.map(r => r.user_id);
    const { data: profiles } = await supabase
      .from('profiles').select('id, username, elo_rating').in('id', ids);
    const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
    setReceived(
      rows.map(r => ({ friendshipId: r.id, profile: profileMap[r.user_id] })).filter(r => r.profile)
    );
  }

  async function fetchSentRequests() {
    const { data: rows } = await supabase
      .from('friendships').select('id, friend_id').eq('user_id', user.id).eq('status', 'pending');
    if (!rows?.length) { setSent([]); return; }
    const ids = rows.map(r => r.friend_id);
    const { data: profiles } = await supabase
      .from('profiles').select('id, username, elo_rating').in('id', ids);
    const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
    setSent(
      rows.map(r => ({ friendshipId: r.id, profile: profileMap[r.friend_id] })).filter(r => r.profile)
    );
  }

  // ── Invite link: process ?invite=username on page load ─────
  // Creates a friend request FROM the inviter TO the current user,
  // so the current user sees it in their Received section.
  async function processInvite(username) {
    // Strip the ?invite= param immediately so reloading doesn't re-trigger this
    navigate('/friends', { replace: true });

    // Look up the inviter by username
    const { data: inviter } = await supabase
      .from('profiles').select('id').eq('username', username).maybeSingle();
    if (!inviter || inviter.id === user.id) return;

    // process_friend_invite handles the duplicate check internally
    await supabase.rpc('process_friend_invite', { p_inviter_id: inviter.id });
    await loadAll();
    // Show the Pending tab so the user sees the incoming request
    setActiveTab('requests');
  }

  // ── Actions ────────────────────────────────────────────────
  async function handleAddFriend(profileId) {
    await supabase.rpc('send_friend_request', { p_friend_id: profileId });
    setSearchStatuses(prev => ({ ...prev, [profileId]: 'pending' }));
    fetchSentRequests();
  }

  async function handleRespond(friendshipId, accept) {
    await supabase.rpc('respond_to_friend_request', { p_friendship_id: friendshipId, p_accept: accept });
    loadAll();
    // Tell the Navbar to refresh its notification count so the red dot clears
    window.dispatchEvent(new CustomEvent('ranktt:friendResolved'));
  }

  async function handleCancel(friendshipId) {
    await supabase.rpc('cancel_friend_request', { p_friendship_id: friendshipId });
    fetchSentRequests();
  }

  async function handleUnfriend(friendId) {
    await supabase.rpc('remove_friend', { p_friend_id: friendId });
    fetchFriends();
  }

  function copyInviteLink() {
    const link = `${window.location.origin}/friends?invite=${myUsername}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // ── Derived values ─────────────────────────────────────────
  const pendingCount = received.length + sent.length;
  const noActivity   = friends.length === 0 && received.length === 0 && sent.length === 0;

  // ── Render ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="friends-page">
        <div className="friends-container">
          <div className="friends-loading">
            <span className="friends-loading-star">★</span>
            <span className="friends-loading-star">★</span>
            <span className="friends-loading-star">★</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="friends-page">
      <div className="friends-container">
        <h1 className="friends-title">Friends &amp; Rivals</h1>

        {/* ── Search ─────────────────────────────────────── */}
        <div className="friends-search-section">
          <div className="friends-search-wrap">
            <svg className="friends-search-icon" width="15" height="15" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="friends-search-input"
              placeholder="Search players by name or username…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>

          {/* Search results */}
          {query.trim().length >= 2 && (
            <div className="friends-search-results">
              {searchLoading && <p className="friends-search-empty">Searching…</p>}
              {!searchLoading && searchResults.length === 0 && (
                <p className="friends-search-empty">No players found.</p>
              )}
              {searchResults.map(p => {
                const status = searchStatuses[p.id];
                return (
                  <div key={p.id} className="friends-search-row">
                    <div className="friends-search-avatar">{p.username[0].toUpperCase()}</div>
                    <div className="friends-search-name-col">
                      <Link to={`/profile/${p.username}`} className="friends-search-name">
                        {p.username}
                      </Link>
                      <span className="friends-search-elo">{p.elo_rating}</span>
                    </div>
                    <div className="friends-search-actions">
                      <button
                        className="friends-btn friends-btn--report"
                        onClick={() => { setReportTarget(p); setReportModalOpen(true); }}
                      >
                        Report Match
                      </button>
                      {status === 'accepted' ? (
                        <span className="friends-btn friends-btn--already">Friends</span>
                      ) : status === 'pending' ? (
                        <span className="friends-btn friends-btn--pending">Pending</span>
                      ) : (
                        <button
                          className="friends-btn friends-btn--add"
                          onClick={() => handleAddFriend(p.id)}
                        >
                          + Add
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Empty state ─────────────────────────────────── */}
        {noActivity && (
          <div className="friends-empty">
            <p className="friends-empty-title">No friends yet</p>
            <p className="friends-empty-sub">
              Search for players above, or share your invite link to get started.
            </p>
            <div className="friends-invite-box">
              <p className="friends-invite-label">Your invite link</p>
              <div className="friends-invite-row">
                <span className="friends-invite-link">
                  {window.location.origin}/friends?invite={myUsername}
                </span>
                <button className="friends-invite-copy" onClick={copyInviteLink}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="friends-invite-hint">
                Anyone who clicks this link will be prompted to send you a friend request.
                If they don't have an account yet, they'll be taken to sign up first.
              </p>
            </div>
          </div>
        )}

        {/* ── Tabs ────────────────────────────────────────── */}
        {!noActivity && (
          <>
            <div className="friends-tabs">
              <button
                className={`friends-tab ${activeTab === 'friends' ? 'active' : ''}`}
                onClick={() => setActiveTab('friends')}
              >
                Friends {friends.length > 0 && <span className="friends-tab-count">{friends.length}</span>}
              </button>
              <button
                className={`friends-tab ${activeTab === 'requests' ? 'active' : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                Pending {pendingCount > 0 && <span className="friends-tab-count friends-tab-count--pending">{pendingCount}</span>}
              </button>
            </div>

            {/* ── Friends tab ─────────────────────────────── */}
            {activeTab === 'friends' && (
              <div className="friends-list">
                {friends.length === 0 ? (
                  <p className="friends-list-empty">
                    No friends yet — search above or check your requests.
                  </p>
                ) : (
                  friends.map(f => (
                    <div key={f.id} className="friends-row">
                      <div className="friends-row-avatar">{f.username[0].toUpperCase()}</div>
                      <div className="friends-row-name-col">
                        <Link to={`/profile/${f.username}`} className="friends-row-name">
                          {f.username}
                        </Link>
                        <span className="friends-row-elo">{f.elo_rating}</span>
                      </div>
                      <div className="friends-row-actions">
                        <button
                          className="friends-row-btn friends-row-btn--report"
                          onClick={() => { setReportTarget(f); setReportModalOpen(true); }}
                        >
                          Report Match
                        </button>
                        <button
                          className="friends-row-btn friends-row-btn--unfriend"
                          onClick={() => handleUnfriend(f.id)}
                        >
                          Unfriend
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ── Requests tab ────────────────────────────── */}
            {activeTab === 'requests' && (
              <div className="friends-requests">

                {/* Received */}
                {received.length > 0 && (
                  <div className="friends-requests-section">
                    <p className="friends-requests-label">Received</p>
                    {received.map(r => (
                      <div key={r.friendshipId} className="friends-row">
                        <div className="friends-row-avatar">{r.profile.username[0].toUpperCase()}</div>
                        <div className="friends-row-name-col">
                          <Link to={`/profile/${r.profile.username}`} className="friends-row-name">
                            {r.profile.username}
                          </Link>
                          <span className="friends-row-elo">{r.profile.elo_rating}</span>
                        </div>
                        <div className="friends-row-actions">
                          <button
                            className="friends-row-btn friends-row-btn--accept"
                            onClick={() => handleRespond(r.friendshipId, true)}
                          >
                            Accept
                          </button>
                          <button
                            className="friends-row-btn friends-row-btn--decline"
                            onClick={() => handleRespond(r.friendshipId, false)}
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Sent */}
                {sent.length > 0 && (
                  <div className="friends-requests-section">
                    <p className="friends-requests-label">Sent</p>
                    {sent.map(s => (
                      <div key={s.friendshipId} className="friends-row">
                        <div className="friends-row-avatar">{s.profile.username[0].toUpperCase()}</div>
                        <div className="friends-row-name-col">
                          <Link to={`/profile/${s.profile.username}`} className="friends-row-name">
                            {s.profile.username}
                          </Link>
                          <span className="friends-row-elo">{s.profile.elo_rating}</span>
                        </div>
                        <button
                          className="friends-row-btn friends-row-btn--cancel"
                          onClick={() => handleCancel(s.friendshipId)}
                        >
                          Cancel
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {received.length === 0 && sent.length === 0 && (
                  <p className="friends-list-empty">No pending requests.</p>
                )}
              </div>
            )}

            {/* Invite link — always shown at bottom when user has friends */}
            <div className="friends-invite-box friends-invite-box--compact">
              <p className="friends-invite-label">Invite a friend</p>
              <div className="friends-invite-row">
                <span className="friends-invite-link">
                  {window.location.origin}/friends?invite={myUsername}
                </span>
                <button className="friends-invite-copy" onClick={copyInviteLink}>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ReportMatchModal
        isOpen={reportModalOpen}
        onClose={() => { setReportModalOpen(false); setReportTarget(null); }}
        currentUserId={user?.id}
        preSelectedOpponent={reportTarget}
        onMatchSubmitted={() => { setReportModalOpen(false); setReportTarget(null); }}
      />
    </div>
  );
}

export default Friends;
